import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Link as LinkIcon, X } from "lucide-react";
import Button from "../Common/Button";
import { validateFormat } from "../../utils/socialValidation";
import { buildPlatformProfileUrl } from "../../utils/socialVerification";

const PlatformIcon = ({ icon, name }) => {
    if (!icon) return <Globe className="h-5 w-5 text-slate-400" />;
    return (
        <img
            src={icon}
            alt={name}
            className="h-6 w-6 object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
    );
};

const isValidUrl = (value) => /^https?:\/\/\S+$/i.test((value || "").trim());

const AccountSocialModal = ({
    isOpen,
    platform,
    initialHandle = "",
    initialLink = "",
    onSave,
    onClose,
}) => {
    const { t } = useTranslation();
    const [handle, setHandle] = useState(initialHandle);
    const [link, setLink] = useState(initialLink);

    useEffect(() => {
        if (isOpen) {
            setHandle(initialHandle);
            setLink(initialLink || buildPlatformProfileUrl(platform?.key || "", initialHandle));
        }
    }, [isOpen, initialHandle, initialLink, platform]);

    if (!isOpen || !platform) return null;

    const isHandleValid = !handle.trim() || validateFormat(platform.key, handle.trim());
    const isLinkValid = !link.trim() || isValidUrl(link);
    const canSave = handle.trim() && isHandleValid && (!link.trim() || isLinkValid);

    const handleSave = () => {
        if (!canSave) return;
        onSave(platform.key, handle.trim(), link.trim());
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8 sm:px-6 sm:py-10">
            <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.2)] md:max-w-4xl">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-4 top-4 z-40 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow focus:outline-none"
                >
                    <X className="h-5 w-5 text-primary" />
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="relative order-last h-48 w-full overflow-hidden sm:h-56 md:order-last md:h-auto">
                        <img
                            src="/assets/images/common/social-popup.png"
                            alt=""
                            className="h-full w-full object-cover"
                        />
                    </div>

                    <div className="order-first flex min-h-[520px] flex-col p-6 sm:p-8 md:p-12">
                        <h2 className="text-xl font-semibold text-slate-900">{platform.name} account</h2>
                        <p className="mt-2 text-sm text-slate-500">
                            {t("AUTH_SOCIAL_MODAL_EDITOR_SUBTITLE")}
                        </p>

                        <div className="mt-6 flex-1 space-y-4">
                            <div className={`flex items-center gap-3 rounded-xl border bg-input-bg px-4 py-3 ${!isHandleValid ? "border-red-400" : "border-slate-200"}`}>
                                <PlatformIcon icon={platform.icon} name={platform.name} />
                                <input
                                    type="text"
                                    value={handle}
                                    onChange={(e) => setHandle(e.target.value)}
                                    placeholder={`${platform.name} ${t("AUTH_SOCIAL_MODAL_HANDLE_PLACEHOLDER")}`}
                                    className="w-full bg-transparent text-sm text-slate-700 outline-none focus:outline-none"
                                />
                                {handle.trim() ? (
                                    <button
                                        type="button"
                                        onClick={() => setHandle("")}
                                        className="flex h-6 w-6 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                ) : null}
                            </div>
                            {handle.trim() && !isHandleValid ? (
                                <p className="text-xs text-red-500">
                                    {t("AUTH_SOCIAL_MODAL_INVALID_FORMAT")} {platform.name}
                                </p>
                            ) : null}

                            <div className={`flex items-center gap-3 rounded-xl border bg-input-bg px-4 py-3 ${!isLinkValid ? "border-red-400" : "border-slate-200"}`}>
                                <LinkIcon className="h-4 w-4 shrink-0 text-slate-400" />
                                <input
                                    type="url"
                                    value={link}
                                    onChange={(e) => setLink(e.target.value)}
                                    placeholder={t("AUTH_SOCIAL_MODAL_URL_PLACEHOLDER")}
                                    className="w-full bg-transparent text-sm text-slate-700 outline-none focus:outline-none"
                                />
                                {link.trim() ? (
                                    <button
                                        type="button"
                                        onClick={() => setLink("")}
                                        className="flex h-6 w-6 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                ) : null}
                            </div>
                            {link.trim() && !isLinkValid ? (
                                <p className="text-xs text-red-500">{t("AUTH_SOCIAL_MODAL_INVALID_URL")}</p>
                            ) : null}
                        </div>

                        <div className="mt-auto pt-6">
                            <Button
                                type="button"
                                onClick={handleSave}
                                disabled={!canSave}
                                className="w-full"
                            >
                                {t("COMMON_SAVE")}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccountSocialModal;
